import { html, type TemplateResult } from "lit";
import { icons } from "../icons.ts";

const COPIED_FOR_MS = 1500;
const ERROR_FOR_MS = 2000;
const COPY_LABEL = "Copy as markdown";
const COPIED_LABEL = "Copied";
const ERROR_LABEL = "Copy failed";

export type CopyButtonOptions = {
  text: () => string;
  label?: string;
  className?: string;
};

export type ClipboardLike = Pick<Clipboard, "writeText">;

/** Minimum surface of the Copy button we touch from the click handler.
 *  Exported so node tests can drive the same code path with a fake element
 *  (no DOM required). */
export type CopyButtonElement = {
  dataset: DOMStringMap;
  disabled: boolean;
  isConnected: boolean;
  title: string;
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
};

/**
 * Writes `text` to the system clipboard.
 *
 * Exported so tests can stub the clipboard without spinning up a DOM.
 * `clipboardOverride` lets unit tests inject a fake `Clipboard` (the real
 * `navigator.clipboard` is unavailable in node).
 */
export async function copyTextToClipboard(
  text: string,
  clipboardOverride?: ClipboardLike,
): Promise<boolean> {
  if (!text) {
    return false;
  }

  const target: ClipboardLike | undefined = clipboardOverride ?? globalThis.navigator?.clipboard;
  if (!target) {
    return false;
  }

  try {
    await target.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function setButtonLabel(button: Pick<CopyButtonElement, "title" | "setAttribute">, label: string) {
  button.title = label;
  button.setAttribute("aria-label", label);
}

/**
 * Shared click-action for the Copy button. Extracted so node tests can drive
 * the exact same code path that the rendered template's `@click` handler runs,
 * without needing a real DOM. The handler:
 *
 *   1. Marks the button busy (idempotent — second click is ignored).
 *   2. Calls `getText()` and writes it via the clipboard API.
 *   3. Reflects success/error on the button (Copied / Copy failed).
 *   4. Clears the busy/error/copied state on the next tick.
 *
 * The selection inside the bubble is intentionally NOT touched here:
 * the per-message Copy button copies the full bubble text (existing UX),
 * while `copySelectionToClipboard` covers the "Copy selection" affordance
 * that uses `getSelection().toString()` to isolate the substring.
 */
export async function runCopyButtonAction(
  btn: CopyButtonElement,
  getText: () => string,
  options?: {
    clipboard?: ClipboardLike;
    idleLabel?: string;
    timeout?: (handler: () => void, ms: number) => unknown;
  },
): Promise<boolean> {
  const idleLabel = options?.idleLabel ?? COPY_LABEL;
  // Resolve the timer once at call time. In the browser this is
  // `window.setTimeout`; in node tests it's whatever the test passes in
  // (or nothing — see the timeout guard below).
  const scheduleTimeout =
    options?.timeout ??
    ((handler, ms) => {
      if (typeof globalThis.setTimeout === "function") {
        return globalThis.setTimeout(handler, ms);
      }
      return undefined;
    });

  if (!btn || btn.dataset.copying === "1") {
    return false;
  }

  btn.dataset.copying = "1";
  btn.setAttribute("aria-busy", "true");
  btn.disabled = true;

  const copied = await copyTextToClipboard(getText(), options?.clipboard);
  if (!btn.isConnected) {
    return copied;
  }

  delete btn.dataset.copying;
  btn.removeAttribute("aria-busy");
  btn.disabled = false;

  if (!copied) {
    btn.dataset.error = "1";
    setButtonLabel(btn, ERROR_LABEL);

    scheduleTimeout(() => {
      if (!btn.isConnected) {
        return;
      }
      delete btn.dataset.error;
      setButtonLabel(btn, idleLabel);
    }, ERROR_FOR_MS);
    return false;
  }

  btn.dataset.copied = "1";
  setButtonLabel(btn, COPIED_LABEL);

  scheduleTimeout(() => {
    if (!btn.isConnected) {
      return;
    }
    delete btn.dataset.copied;
    setButtonLabel(btn, idleLabel);
  }, COPIED_FOR_MS);

  return true;
}

export function createCopyButton(options: CopyButtonOptions): TemplateResult {
  const idleLabel = options.label ?? COPY_LABEL;
  return html`
    <button
      class=${options.className ?? "chat-copy-btn"}
      type="button"
      title=${idleLabel}
      aria-label=${idleLabel}
      @click=${(e: Event) => {
        void runCopyButtonAction(e.currentTarget as HTMLButtonElement, options.text, {
          idleLabel,
        });
      }}
    >
      <span class="chat-copy-btn__icon" aria-hidden="true">
        <span class="chat-copy-btn__icon-copy">${icons.copy}</span>
        <span class="chat-copy-btn__icon-check">${icons.check}</span>
      </span>
    </button>
  `;
}

export function renderCopyAsMarkdownButton(markdown: string): TemplateResult {
  return createCopyButton({ text: () => markdown, label: COPY_LABEL });
}

/**
 * Returns the currently selected text inside `scope` (or `document` when no
 * scope is provided). Empty string when there is no live selection.
 *
 * Used by the "Copy selection" affordance on message bubbles so that
 * `navigator.clipboard.writeText(getSelection().toString())` only ever carries
 * the substring the user actually highlighted — never the entire bubble and
 * never anything bleeding into the composer textarea (which lives outside the
 * bubble).
 */
export function readSelectionWithin(scope: Element | Document | null = null): string {
  try {
    const selection = scope?.ownerDocument?.getSelection?.() ?? globalThis.getSelection?.();
    if (!selection || selection.rangeCount === 0) return "";
    const text = selection.toString();
    if (!scope) return text;
    // Guard against the selection spanning outside the bubble (e.g. the user
    // started dragging inside the bubble and ended up in the composer).
    const anchorNode = selection.anchorNode;
    const focusNode = selection.focusNode;
    if (!anchorNode || !focusNode) return "";
    if (!scope.contains(anchorNode) || !scope.contains(focusNode)) {
      return "";
    }
    return text;
  } catch {
    return "";
  }
}

/**
 * Writes the text the user has currently selected within `scope` to the
 * system clipboard. Returns `true` when something was copied, `false` when
 * there is no selection inside the bubble (so the caller can fall back to
 * the full-bubble Copy button).
 */
export async function copySelectionWithin(
  scope: Element | Document | null,
  clipboard?: ClipboardLike,
): Promise<boolean> {
  const selected = readSelectionWithin(scope);
  if (!selected) return false;
  return copyTextToClipboard(selected, clipboard);
}
